// DetailsPanel shows further information for a particular intervention
// (also possibly for an entire intervention group/category?).
//
// It can be dismissed by clicking the close button or pressing the escape key,
// and its contents are updated if it's open while another intervention outside
// of the panel is selected.
//
// If the panel is open while the risk factor selection changes, it reflects any
// updates in the risk factors associated with the displayed intervention.
//
// If the intervention is not related to any of the currently selected risk
// factors, it replaces the panel's risk factor list with a note to that effect.


import {
  debounce,
  getTransitionDuration,
  isValidInterventionId,
  setupLogger,
} from '../utils.js'
import {
  interventionDetailsTemplate,
} from '../templates.js'

const { logger, loggerError } = setupLogger('DetailsPanel', 'forestgreen')


export default class DetailsPanel extends HTMLElement {
  static observedAttributes = ['details-for']

  constructor() {
    super()
    logger(this)

    if (!this.hasAttribute('details-for')) {
      this.setAttribute('details-for', '')
    }
    this.forAssessment = this.getAttribute('for-assessment')
    this.selectedRiskFactors = []

    this.dialog = this.dialogTemplate()
    this.dialog.innerHTML = this.loadingTemplate()
    this.append(this.dialog)
    this.animationDuration = getTransitionDuration(this.dialog)

    this.bindKeypress = this.handleKeypress.bind(this)
  }

  connectedCallback() {
    document.addEventListener('click', (event) => {
      if (!event.target.dataset.detailsFor) return

      this.setAttribute('details-for', event.target.dataset.detailsFor)
      this.openPanel()
    })

    document.addEventListener('click', (event) => {
      if (!event.target.hasAttribute('data-panel-close')) return

      this.closePanel()
    })

    // Debounce the listener's callback a bit to batch updates:
    document.addEventListener('reef:signal-risk-selection', debounce((event) => {
      logger('heard reef:signal-risk-selection', event.detail)
      this.selectedRiskFactors = Array.from(event.detail)
      if (this.dialog.open) {
        this.renderDialog(this.getAttribute('details-for'))
      }
    }, 50))

  }

  attributeChangedCallback(attr, oldValue, newValue) {
    if (attr !== 'details-for') return

    if (newValue !== oldValue) {
      this.renderDialog(newValue)
    }
  }

  openPanel() {
    if (this.dialog.open) return

    // Make sure this class is removed:
    this.dialog.classList.remove('is-closing')

    this.addEventListener('keydown', this.bindKeypress)
    this.dialog.show()

    // Animate the dialog opening:
    this.dialog.classList.add('is-open')
  }

  closePanel() {
    // Animate the dialog closing:
    this.dialog.classList.add('is-closing')
    this.dialog.classList.remove('is-open')

    // If there's an animation duration, give it time to finish before hiding the dialog:
    setTimeout(() => {
      this.dialog.classList.remove('is-closing')
      this.dialog.close()
    }, this.animationDuration)

    this.removeEventListener('keydown', this.bindKeypress)
  }

  handleKeypress = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.closePanel()
    }
  }

  renderDialog(interventionTag) {
    if (!interventionTag) {
      this.dialog.innerHTML = this.innerTemplate(
        'Intervention details',
        `<p>Please select an intervention ID to view its details.</p>`
      )
      return
    }

    const [category, id] = interventionTag.split('-')
    if (isValidInterventionId(category, id)) {
      this.dialog.innerHTML = this.innerTemplate(
        'Intervention details',
        interventionDetailsTemplate(category, id, this.forAssessment, this.selectedRiskFactors)
      )
    } else {
      this.dialog.innerHTML = this.innerTemplate(
        'Error',
        `<p>Invalid intervention ID:</p><p>${category.toUpperCase()} P${id}</p>`
      )
    }
  }

  dialogTemplate() {
    const dialogEl = document.createElement('dialog')
    dialogEl.classList.add('details-panel')
    dialogEl.setAttribute('data-panel', this.forAssessment)
    return dialogEl
  }

  innerTemplate(heading, body) {
    return `<div class="details-panel-inner">
        <header class="details-panel-header">
          <h3 class="heading u-h4">
            ${heading}
          </h3>
          <button data-panel-close class="btn-sm">
            <ph-x size="1.5em" weight="bold" aria-label="Close"></ph-x>
          </button>
        </header>
        <div class="details-panel-body flow">
          ${body}
        </div>
      </div>
    `
  }

  loadingTemplate() {
    return this.innerTemplate(
      'Intervention details',
      '<p>Loading details…</p>'
    )
  }
}
